import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { RESOURCE_LIMIT_EXCEEDED_MESSAGE } from 'constants/errorMessages';
import { REGION_SUBSCRIPTION_PLANS } from 'constants/routes';
import { useContext } from 'react';
import { useAuthStore } from 'stores/authStore';
import { getPageParamFromLastPage } from 'utils/getPageParamFromLastPage';
import { openUpgradeTierModal } from 'utils/handleTierExceededError';
import RETAILERS_KEYS from '../retailers.keys';
import RETAILERS_MUTATIONS from '../retailers.mutation';
import RETAILERS_QUERIES from '../retailers.queries';

export const useFetchRetailers = ({ page, pageSize, search, ordering, filters } = {}) =>
  useQuery({
    queryKey: RETAILERS_KEYS.RETAILERS_PAGINATED({ page, pageSize, search, ordering, filters }),
    queryFn: () => RETAILERS_QUERIES.FETCH_RETAILERS({ page, pageSize, search, ordering, filters }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchRetailersInfinite = ({ pageSize, search, ordering, filters } = {}) =>
  useInfiniteQuery({
    queryKey: RETAILERS_KEYS.RETAILERS_INFINITE({ pageSize, search, ordering, filters }),
    queryFn: ({ pageParam = 1 }) =>
      RETAILERS_QUERIES.FETCH_RETAILERS({
        page: pageParam,
        pageSize,
        search,
        ordering,
        filters,
      }),
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchRetailer = (uid) =>
  useQuery({
    queryKey: RETAILERS_KEYS.RETAILER(uid),
    queryFn: () => RETAILERS_QUERIES.FETCH_RETAILER(uid),
    enabled: !!uid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchRetailerDetailsLooks = (uid) =>
  useQuery({
    queryKey: RETAILERS_KEYS.RETAILER_LOOKS(uid),
    queryFn: () => RETAILERS_QUERIES.FETCH_RETAILER_LOOKS(uid),
    enabled: !!uid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchRetailerDetailsFavoriteProducts = (uid) =>
  useQuery({
    queryKey: RETAILERS_KEYS.RETAILER_FAVORITE_PRODUCTS(uid),
    queryFn: () => RETAILERS_QUERIES.FETCH_RETAILER_FAVORITE_PRODUCTS(uid),
    enabled: !!uid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useUpdateRetailer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: RETAILERS_MUTATIONS.UPDATE_RETAILER,
    onMutate: async (updatedRetailer) => {
      await queryClient.cancelQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
      await queryClient.cancelQueries({
        queryKey: RETAILERS_KEYS.RETAILER(updatedRetailer.uid),
      });

      const previousRetailers = queryClient.getQueriesData({
        queryKey: RETAILERS_KEYS.RETAILERS(),
      });
      const previousRetailer = queryClient.getQueryData(
        RETAILERS_KEYS.RETAILER(updatedRetailer.uid),
      );

      queryClient.setQueriesData({ queryKey: RETAILERS_KEYS.RETAILERS() }, (oldData) => {
        if (!oldData) return oldData;

        if (oldData.results) {
          return {
            ...oldData,
            results: oldData.results.map((retailer) =>
              retailer.uid === updatedRetailer.uid ? { ...retailer, ...updatedRetailer } : retailer,
            ),
          };
        }

        if (Array.isArray(oldData)) {
          return oldData.map((retailer) =>
            retailer.uid === updatedRetailer.uid ? { ...retailer, ...updatedRetailer } : retailer,
          );
        }

        return oldData;
      });

      queryClient.setQueryData(RETAILERS_KEYS.RETAILER(updatedRetailer.uid), (oldData) =>
        oldData ? { ...oldData, ...updatedRetailer } : oldData,
      );

      return { previousRetailers, previousRetailer };
    },

    onError: (_, updatedRetailer, context) => {
      if (context?.previousRetailers) {
        context.previousRetailers.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousRetailer) {
        queryClient.setQueryData(
          RETAILERS_KEYS.RETAILER(updatedRetailer.uid),
          context.previousRetailer,
        );
      }
    },

    onSettled: (_, __, { uid }) => {
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILER(uid) });
    },
  });
};

export const useUpdateRetailerPicture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: RETAILERS_MUTATIONS.UPDATE_RETAILER_PICTURE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
    },
  });
};

export const useUpdateRetailerTrackingId = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: RETAILERS_MUTATIONS.UPDATE_RETAILER_TRACKING_ID,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
    },
  });
};

export const useUpdateRetailerConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: RETAILERS_MUTATIONS.UPDATE_RETAILER_CONFIG,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
    },
  });
};

export const useDeleteRetailerDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: RETAILERS_MUTATIONS.DELETE_RETAILER,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
    },
  });
};

export const useDeleteRetailerProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: RETAILERS_MUTATIONS.DELETE_RETAILER_PROFILE,
    onSuccess: (_, uid) => {
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
      queryClient.removeQueries({ queryKey: RETAILERS_KEYS.RETAILER(uid) });
    },
  });
};

export const useInviteRetailer = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const { closePopup } = useContext(PopupContext);

  return useMutation({
    mutationFn: RETAILERS_MUTATIONS.INVITE_RETAILER,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
      closePopup?.();
    },
    onError: (error, variables) => {
      if (error.message === RESOURCE_LIMIT_EXCEEDED_MESSAGE) {
        closePopup?.();
        const selectedRegion = variables.region_uid;
        const limit = user?.admin_details.brand.regions.find(
          (region) => region.uid === selectedRegion,
        ).subscription.max_retailers;
        const upgradeUrl = REGION_SUBSCRIPTION_PLANS.replace(':id', variables.region_uid);

        openUpgradeTierModal({
          resourceType: 'retailers',
          upgradeUrl,
          currentLimit: limit,
        });
      }
    },
  });
};

export const useInviteRetailers = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const { closePopup } = useContext(PopupContext);

  return useMutation({
    mutationFn: RETAILERS_MUTATIONS.INVITE_RETAILERS,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAILERS_KEYS.RETAILERS() });
      closePopup?.();
    },
    onError: (error, variables) => {
      if (error.message === RESOURCE_LIMIT_EXCEEDED_MESSAGE) {
        closePopup?.();
        const selectedRegion = variables.region_uid;
        const limit = user?.admin_details?.brand.regions.find(
          (region) => region.uid === selectedRegion,
        ).subscription.max_retailers;
        const upgradeUrl = REGION_SUBSCRIPTION_PLANS.replace(':id', variables.region_uid);

        openUpgradeTierModal({
          resourceType: 'retailers',
          upgradeUrl,
          currentLimit: limit,
        });
      }
    },
  });
};
